import {
  type HardwareConfig,
  isHardwareConfig,
  isOperationalNumericConfigValue,
  normalizeConfigIdentifier,
  type TuningTargetConfig,
} from './schema';

export class HardwareConfigValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HardwareConfigValidationError';
  }
}

export async function loadHardwareConfig(url = './hardware_config.json'): Promise<HardwareConfig> {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load hardware config (${response.status}).`);
  }

  const data = await response.json();
  if (!isHardwareConfig(data)) {
    throw new HardwareConfigValidationError('Hardware config schema is invalid.');
  }

  validateHardwareConfig(data);
  return data;
}

export function validateHardwareConfig(config: HardwareConfig): void {
  if (config.hardware_revisions.length === 0) {
    throw new HardwareConfigValidationError('Hardware config must include at least one revision.');
  }

  const revisionIds = new Set<string>();
  for (const revision of config.hardware_revisions) {
    const normalizedRevisionId = normalizeConfigIdentifier(revision.revision_id);
    if (!normalizedRevisionId) {
      throw new HardwareConfigValidationError('Hardware revision id cannot be empty.');
    }

    if (revisionIds.has(normalizedRevisionId)) {
      throw new HardwareConfigValidationError(`Duplicate hardware revision id "${revision.revision_id}".`);
    }
    revisionIds.add(normalizedRevisionId);

    if (revision.tuning_targets.length === 0) {
      throw new HardwareConfigValidationError(
        `Hardware revision "${revision.revision_id}" must include at least one tuning target.`
      );
    }

    const targetIds = new Set<string>();
    const targetPriorities = new Set<number>();
    const windows: Array<{ targetId: string; lowHz: number; highHz: number }> = [];

    for (const target of revision.tuning_targets) {
      const normalizedTargetId = normalizeConfigIdentifier(target.target_id);
      if (!normalizedTargetId) {
        throw new HardwareConfigValidationError(
          `Target id cannot be empty in revision "${revision.revision_id}".`
        );
      }

      if (targetIds.has(normalizedTargetId)) {
        throw new HardwareConfigValidationError(
          `Duplicate target id "${target.target_id}" in revision "${revision.revision_id}".`
        );
      }
      targetIds.add(normalizedTargetId);

      if (!Number.isInteger(target.priority) || target.priority < 0) {
        throw new HardwareConfigValidationError(
          `Target "${target.target_id}" in revision "${revision.revision_id}" has invalid priority "${target.priority}".`
        );
      }

      if (targetPriorities.has(target.priority)) {
        throw new HardwareConfigValidationError(
          `Duplicate target priority "${target.priority}" in revision "${revision.revision_id}".`
        );
      }
      targetPriorities.add(target.priority);

      const window = toOperationalWindow(target);
      if (window) {
        windows.push(window);
      }
    }

    for (let i = 0; i < windows.length; i += 1) {
      for (let j = i + 1; j < windows.length; j += 1) {
        const left = windows[i];
        const right = windows[j];
        if (rangesOverlap(left.lowHz, left.highHz, right.lowHz, right.highHz)) {
          throw new HardwareConfigValidationError(
            `Overlapping tuning windows in revision "${revision.revision_id}" between "${left.targetId}" and "${right.targetId}".`
          );
        }
      }
    }
  }
}

function toOperationalWindow(
  target: TuningTargetConfig
): { targetId: string; lowHz: number; highHz: number } | null {
  if (!isOperationalNumericConfigValue(target.center_frequency_hz)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(target.search_bandwidth_hz)) {
    return null;
  }

  const centerHz = target.center_frequency_hz;
  const bandwidthHz = target.search_bandwidth_hz;
  if (bandwidthHz < 0) {
    throw new HardwareConfigValidationError(
      `Target "${target.target_id}" has invalid negative search bandwidth.`
    );
  }

  return {
    targetId: target.target_id,
    lowHz: centerHz - bandwidthHz,
    highHz: centerHz + bandwidthHz,
  };
}

function rangesOverlap(leftMin: number, leftMax: number, rightMin: number, rightMax: number): boolean {
  return leftMin <= rightMax && rightMin <= leftMax;
}
